if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.charmosoCartLinkMode = this.dataset.charmosoCartLinkMode === 'true';
        this.charmosoInlineQuantityEnabled = this.dataset.charmosoInlineQuantity === 'true';
        this.charmosoInlineQuantity = this.querySelector('[data-charmoso-inline-qty]');
        this.charmosoInlineQuantityValue = this.querySelector('[data-charmoso-quantity-value]');
        this.charmosoVariantId = this.dataset.charmosoVariantId;
        this.charmosoQuantitySyncTimer = null;
        this.charmosoQuantityRequestId = 0;
        this.charmosoLastConfirmedQuantity = parseInt(this.dataset.charmosoCurrentQuantity || '0', 10) || 0;
        this.charmosoDesiredQuantity = this.charmosoLastConfirmedQuantity;
        this.charmosoAddPending = false;

        if (this.charmosoInlineQuantityEnabled) this.setupCharmosoInlineQuantity();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.charmosoCartLinkMode) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        this.handleErrorMessage();

        let charmosoQuantityBeforeAdd = null;
        if (this.charmosoInlineQuantityEnabled) {
          charmosoQuantityBeforeAdd = this.getCharmosoInlineQuantity();
          this.charmosoAddPending = true;
          this.charmosoDesiredQuantity = charmosoQuantityBeforeAdd + 1;
          this.setCharmosoInlineQuantity(this.charmosoDesiredQuantity);
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (this.charmosoInlineQuantityEnabled && charmosoQuantityBeforeAdd !== null) {
                this.setCharmosoInlineQuantity(charmosoQuantityBeforeAdd);
                this.charmosoDesiredQuantity = charmosoQuantityBeforeAdd;
                this.charmosoAddPending = false;
              }
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            if (this.charmosoInlineQuantityEnabled) {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCharmosoInlineQuantity(response);
              });
              return;
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            if (this.charmosoInlineQuantityEnabled && charmosoQuantityBeforeAdd !== null) {
              this.setCharmosoInlineQuantity(charmosoQuantityBeforeAdd);
              this.charmosoDesiredQuantity = charmosoQuantityBeforeAdd;
              this.charmosoAddPending = false;
            }
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      setupCharmosoInlineQuantity() {
        const currentQuantity = parseInt(this.dataset.charmosoCurrentQuantity || '0', 10) || 0;
        this.charmosoLastConfirmedQuantity = currentQuantity;
        this.charmosoDesiredQuantity = currentQuantity;
        this.setCharmosoInlineQuantity(currentQuantity);

        this.charmosoInlineQuantity?.addEventListener('click', (event) => {
          const minus = event.target.closest('[data-charmoso-quantity-minus]');
          const plus = event.target.closest('[data-charmoso-quantity-plus]');
          if (!minus && !plus) return;

          event.preventDefault();
          const current = this.getCharmosoInlineQuantity();
          const nextQuantity = Math.max(0, current + (plus ? 1 : -1));
          this.updateCharmosoCartQuantity(nextQuantity);
        });
      }

      getCharmosoInlineQuantity() {
        return parseInt(this.charmosoInlineQuantityValue?.textContent || this.dataset.charmosoCurrentQuantity || '0', 10) || 0;
      }

      renderCharmosoInlineQuantity(response) {
        const serverQuantity = parseInt(response.quantity || this.dataset.charmosoCurrentQuantity || '1', 10) || 1;
        this.charmosoLastConfirmedQuantity = serverQuantity;
        this.updateCharmosoCartBubble(response);

        if (this.charmosoDesiredQuantity > serverQuantity) {
          this.charmosoAddPending = false;
          this.updateCharmosoCartQuantity(this.charmosoDesiredQuantity);
          return;
        }

        this.charmosoAddPending = false;
        this.charmosoDesiredQuantity = serverQuantity;
        this.setCharmosoInlineQuantity(serverQuantity);
      }

      setCharmosoInlineQuantity(quantity) {
        if (!this.charmosoInlineQuantity || !this.charmosoInlineQuantityValue) return;

        const safeQuantity = Math.max(0, parseInt(quantity || '0', 10) || 0);
        this.dataset.charmosoCurrentQuantity = String(safeQuantity);
        this.charmosoInlineQuantityValue.textContent = safeQuantity;
        this.charmosoInlineQuantity.classList.toggle('hidden', safeQuantity <= 0);
        if (this.form) this.form.toggleAttribute('hidden', safeQuantity > 0);
      }

      setCharmosoInlineLoading(isLoading) {
        this.charmosoInlineQuantity?.classList.toggle('is-loading', isLoading);
      }

      updateCharmosoCartQuantity(quantity) {
        if (!this.charmosoVariantId) return;

        const safeQuantity = Math.max(0, parseInt(quantity || '0', 10) || 0);
        this.charmosoDesiredQuantity = safeQuantity;
        this.setCharmosoInlineQuantity(safeQuantity);

        if (this.charmosoAddPending) return;

        window.clearTimeout(this.charmosoQuantitySyncTimer);
        this.charmosoQuantitySyncTimer = window.setTimeout(() => {
          this.syncCharmosoCartQuantity(this.charmosoDesiredQuantity);
        }, 120);
      }

      syncCharmosoCartQuantity(quantity) {
        const requestId = ++this.charmosoQuantityRequestId;
        const body = JSON.stringify({
          updates: { [this.charmosoVariantId]: quantity },
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
          .then((response) => response.json())
          .then((cart) => {
            if (requestId !== this.charmosoQuantityRequestId) return;
            if (quantity !== this.charmosoDesiredQuantity) {
              this.updateCharmosoCartBubble(cart);
              return;
            }

            const item = cart.items?.find((cartItem) => cartItem.id === parseInt(this.charmosoVariantId, 10));
            const updatedQuantity = item ? item.quantity : 0;
            this.charmosoLastConfirmedQuantity = updatedQuantity;
            this.charmosoDesiredQuantity = updatedQuantity;
            this.updateCharmosoCartBubble(cart);
            this.setCharmosoInlineQuantity(updatedQuantity);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'charmoso-inline-quantity',
              productVariantId: this.charmosoVariantId,
              cartData: cart,
            });
          })
          .catch((error) => {
            if (requestId !== this.charmosoQuantityRequestId) return;
            console.error(error);
            this.charmosoDesiredQuantity = this.charmosoLastConfirmedQuantity;
            this.setCharmosoInlineQuantity(this.charmosoLastConfirmedQuantity);
          });
      }

      updateCharmosoCartBubble(parsedState) {
        if (!parsedState.sections || !parsedState.sections['cart-icon-bubble']) return;

        const cartIconBubbles = document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-top');
        if (!cartIconBubbles.length) return;

        const html = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
        const source = html.querySelector('.shopify-section');
        const content = source ? source.innerHTML : parsedState.sections['cart-icon-bubble'];
        cartIconBubbles.forEach((cartIconBubble) => {
          cartIconBubble.innerHTML = content;
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.charmosoCartLinkMode ? 'Ir ao carrinho' : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
